import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import AdminTopRight from './AdminTopRight'

type LiveEvent = {
  type: 'typing'|'question'|'answered'|'cursor'|'paste'|'finished'
  index?: number
  value?: string
  id?: string
  answer?: string
  timeMs?: number
  pasted?: boolean
  x?: number
  y?: number
  ts: number
}
type MetaResp = {
  ok: boolean
  sessionId: string
  exam: string
  extension?: string
  questions: { id: string; imageUrl?: string|null }[]
}

export default function LivePage() {
  const { sessionId = '' } = useParams()
  const [meta, setMeta] = useState<MetaResp | null>(null)
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [typing, setTyping] = useState('')
  const [cursor, setCursor] = useState<{x:number,y:number}|null>(null)
  const [qIndex, setQIndex] = useState(0)
  const [showModal, setShowModal] = useState(true)
  //@ts-ignore
//   const [me,setMe] = useState<Me|null>(null)
//   const [authChecked,setAuthChecked] = useState(false)

//     useEffect(()=>{ fetch('/api/me').then(r=>r.json()).then(d=>{setMe(d.me||null)}).finally(()=>setAuthChecked(true))},[])
//     if (!authChecked) return <Shell><p className="muted">Sprawdzam dostęp…</p></Shell>
// if (!me || !me.isAdmin) return <Shell><p>Brak dostępu. <a href="/auth/login">Zaloguj przez Discord</a></p></Shell>

  useEffect(() => {
    fetch(`/api/live-meta/${sessionId}`).then(r=>r.json()).then(setMeta)
  }, [sessionId])

  useEffect(() => {
    const es = new EventSource(`/api/live-stream/${sessionId}`)
    es.onmessage = (e) => {
      try {
        const ev: LiveEvent = JSON.parse(e.data)
        if (ev.type === 'typing') setTyping(ev.value || '')
        if (ev.type === 'question' && typeof ev.index === 'number') { setQIndex(ev.index); setTyping('') }
        if (ev.type === 'cursor' && typeof ev.x === 'number' && typeof ev.y === 'number') {
          // ev.x, ev.y są normalizowane do rozmiaru okna kandydata (0..1)
          setCursor({ x: ev.x, y: ev.y })
        }
        setEvents(prev => [ev, ...prev].slice(0, 300))
      } catch {}
    }
    return () => es.close()
  }, [sessionId])

  const total = meta?.questions.length ?? 0
  const percent = Math.round((qIndex / Math.max(1, total)) * 100)

  const imgSrc = useMemo(() => {
    if (!meta) return ''
    const q = meta.questions[qIndex]; if (!q) return ''
    return q.imageUrl || `/assets/${meta.exam}/${q.id}.${meta.extension || 'jpg'}`
  }, [meta, qIndex])

  return (
    <Shell>
        <AdminTopRight />
      {/* globalny kursor – po całym oknie (używa vw/vh, nie skaluje się do obrazka) */}
      {!!cursor && (
        <div
          className="cursor-global"
          style={{ left: `${(cursor.x||0)*100}vw`, top: `${(cursor.y||0)*100}vh` }}
        />
      )}

      <div className="top">
        <div className="title">
          <h2>Egzamin {meta?.exam || ''} – LIVE</h2>
          <span className="badge">podgląd</span>
        </div>
        <div className="muted">{Math.min(qIndex + 1, total)} / {total}</div>
      </div>
      <div className="progress"><div className="bar" style={{ width: `${percent}%` }} /></div>

      <div className="card">
        <div className="cols">
          {/* LEWO: obraz 1:1 jak na stronie egzaminu */}
          <div className="left">
            <div className="imageWrap">
              {imgSrc ? <img src={imgSrc} alt="Miejscówka" /> : <div className="ph">Brak obrazu</div>}
            </div>
            {/* timeru tu nie ma — to tylko podgląd; chcesz, dorobię */}
          </div>

          {/* PRAWO: „ghost” input 1:1 jak kandydat (tylko do odczytu) */}
          <div className="right">
            <label>Wpis kandydata (na żywo):</label>
            <div className="inputGhost">{typing || <span className="muted">…</span>}</div>
            <p className="muted" style={{marginTop:10}}>Podgląd nie ingeruje w egzamin; pokazuje wpisywany tekst i pozycję kursora.</p>
          </div>
        </div>
      </div>

      {/* FAB do otwierania modala zdarzeń */}
      {!showModal && (
        <button className="fab" onClick={()=>setShowModal(true)}>
          Zdarzenia • {events.length}
        </button>
      )}

      {/* MODAL zdarzeń w prawym dolnym rogu */}
      {showModal && (
        <div className="modal">
          <div className="modalHeader">
            <b>Zdarzenia</b>
            <button className="chip" onClick={()=>setShowModal(false)}>Zwiń</button>
          </div>
          <div className="modalBody">
            {events.map((ev, i) => (
              <div className="ev" key={i}>
                <div className="evTop">
                  <span className="time">{new Date(ev.ts).toLocaleTimeString()}</span>
                  <span className="type">{ev.type}{typeof ev.index==='number' ? ` (q${ev.index+1})` : ''}</span>
                </div>
                {ev.value && <div className="kv"><span>typing</span><code>{ev.value}</code></div>}
                {ev.answer && (
                  <div className="kv">
                    <span>answer</span>
                    <code>{ev.answer}</code>
                    {ev.pasted && <em>pasted</em>}
                    {ev.timeMs!=null && <small>{ev.timeMs} ms</small>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  )
}

function Shell({children}:{children:React.ReactNode}) {
  return (
    <div className="wrap">
      <style>{css}</style>
      {children}
    </div>
  )
}

/* identyczna baza stylu jak w ExamPage + modal i globalny kursor */
const css = `
:root{--bg:#0b0d12;--card:#111525;--card2:#0e1220;--border:#1f2430;--text:#e8e8ec;--muted:#a6a8ad;--acc:#7a2cff;--ok:#2e9c4b}
*{box-sizing:border-box}
body{background:linear-gradient(180deg,#0b0d12,#0a0c14);color:var(--text);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;margin:0}
.wrap{max-width:1000px;margin:0 auto;padding:24px}
.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.title{display:flex;gap:10px;align-items:center}
.badge{padding:4px 10px;background:#1b1f2d;border:1px solid #2a3042;border-radius:999px;color:var(--muted);font-size:12px}
.muted{color:var(--muted)}
.progress{height:8px;background:#151a27;border:1px solid #20263a;border-radius:999px;overflow:hidden;margin:8px 0 18px}
.bar{height:100%;background:var(--acc);width:0%}
.card{background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.45);padding:20px}
.cols{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.imageWrap{position:relative;min-height:360px;border-radius:14px;overflow:hidden;border:1px solid #262c3f;background:var(--card2)}
.imageWrap img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.inputGhost{min-height:44px;padding:10px 12px;border-radius:12px;border:1px solid #2b3242;background:#0e1220;color:#fff;font-size:16px}

/* GLOBALNY KURSOR – po całym oknie (nie w imageWrap) */
.cursor-global{
  position:fixed;
  width:22px;height:22px;border-radius:999px;
  transform:translate(-50%,-50%);
  background:rgba(255,255,255,.14);
  border:2px solid rgba(255,255,255,.9);
  box-shadow:0 0 0 6px rgba(122,44,255,.25);
  pointer-events:none;
  transition:left .05s linear, top .05s linear;
  z-index:1000;
}

/* MODAL zdarzeń (prawy dół) */
.fab{
  position:fixed; right:20px; bottom:20px;
  background:var(--acc); color:#fff; border:0; border-radius:999px;
  padding:10px 14px; font-weight:700; cursor:pointer; box-shadow:0 10px 30px rgba(0,0,0,.4); z-index:1001;
}
.modal{
  position:fixed; right:20px; bottom:20px; width:360px; max-height:65vh;
  display:flex; flex-direction:column; overflow:hidden;
  background:#0f1422; border:1px solid #1f2430; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.55);
  z-index:1001;
}
.modalHeader{display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-bottom:1px solid #1e2536}
.chip{background:#1b2440;color:#e8e8ec;border:1px solid #2a3046;border-radius:999px;padding:6px 10px;cursor:pointer}
.modalBody{padding:8px 10px;overflow:auto;display:flex;flex-direction:column;gap:8px}
.ev{background:#0b1021;border:1px solid #1e2536;border-radius:12px;padding:8px}
.evTop{display:flex;gap:8px;align-items:center;margin-bottom:4px}
.evTop .time{font-size:12px;color:#a6a8ad}
.evTop .type{font-size:13px}
.kv{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.kv code{background:#0e1321;padding:2px 6px;border-radius:6px;border:1px solid #20263a}
.kv em{color:#ffbe76;font-style:normal}
.kv small{color:#a6a8ad}
`
